<template>
  <div class="card card-body shadow-sm mt-3">
    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-2">
      <h4 class="mb-0">
        Pagamentos da Receita nº {{ receitaId }}
      </h4>
      <button class="btn btn-secondary" @click="voltar">Voltar</button>
    </div>

    <!-- Estado de carregamento / erro / vazio -->
    <div v-if="loading"><LoadingSpinner /></div>
    <div v-else-if="erro" class="alert alert-danger">{{ erro }}</div>
    <div v-else-if="pagamentos.length === 0" class="alert alert-warning">
      Nenhum pagamento encontrado para esta receita.
    </div>

    <!-- Tabela de pagamentos -->
    <div v-else class="table-responsive">
      <table class="table table-bordered align-middle">
        <thead class="table-light">
          <tr>
            <th style="width: 110px;">Parcela</th>
            <th>Forma</th>
            <th style="width: 130px;">Vencimento</th>
            <th style="width: 130px;">Pagamento</th>
            <th style="width: 130px;">Recebimento</th>
            <th class="text-end" style="width: 140px;">Valor</th>
            <th>Conta Financeira</th>
            <th v-if="mostraCheque">Banco</th>
            <th v-if="mostraCheque">Agência</th>
            <th v-if="mostraCheque">Cheque nº</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(p, idx) in pagamentos" :key="p.id ?? idx">
            <td>#{{ p.parcela ?? idx + 1 }}</td>
            <td>{{ p.forma_pagamento || '—' }}</td>
            <td>{{ formatData(p.data_vencimento) }}</td>
            <td>
              <span v-if="p.data_pagamento">{{ formatData(p.data_pagamento) }}</span>
              <span v-else class="text-danger">Pendente</span>
            </td>
            <td>
              <span v-if="p.data_recebimento">{{ formatData(p.data_recebimento) }}</span>
              <span v-else class="text-danger">Pendente</span>
            </td>
            <td class="text-end fw-semibold">{{ formatValor(p.valor) }}</td>
            <td>{{ textoConta(p) }}</td>
            <td v-if="mostraCheque">{{ p.banco_numero || '—' }}</td>
            <td v-if="mostraCheque">{{ p.agencia || '—' }}</td>
            <td v-if="mostraCheque">{{ p.cheque_numero || '—' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Rodapé simples (opcional) -->
    <div v-if="!loading && pagamentos.length" class="mt-3 d-flex justify-content-end">
      <div class="text-end">
        <div><strong>Total de parcelas:</strong> {{ pagamentos.length }}</div>
        <div><strong>Total planejado:</strong> {{ formatValor(totalPrevisto) }}</div>
        <div><strong>Total recebido:</strong> {{ formatValor(totalRecebido) }}</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useReceitasPermissions } from '@/composables/useReceitasPermissions';
const { fetchPermissions, hasPermission } = useReceitasPermissions();
import { useRoute, useRouter } from 'vue-router';
import axios from '@/services/axios';
import LoadingSpinner from '@/utils/LoadingSpinner.vue';
import globalUtils from '@/utils/globalUtils';

// ===== Rota e navegação =====
const route = useRoute();
const router = useRouter();
const receitaId = computed(() => route.params.id);
// origem: 'receita_geral' | 'aula_avulsa' | 'aula_experimental' | 'venda'
const origem = computed(() => String(route.query.origem || 'receita_geral').toLowerCase());

// ===== Estado =====
const loading = ref(true);
const erro = ref('');
const pagamentos = ref([]);  // array de pagamentos normalizado
const contas = ref([]);      // contas financeiras p/ lookup

// Mostrar colunas de cheque se ao menos um pagamento tiver forma 'cheque'
const mostraCheque = computed(() =>
  pagamentos.value.some(p => String(p.forma_pagamento || '').toLowerCase() === 'cheque')
);

// Totais
const totalPrevisto = computed(() =>
  somaValores(pagamentos.value.map(p => p.valor))
);
const totalRecebido = computed(() =>
  somaValores(pagamentos.value.filter(p => !!p.data_recebimento).map(p => p.valor))
);

// ===== Mapas de endpoints por origem =====
// Ajuste os caminhos abaixo conforme seu backend:
const ENDPOINT_PAGAMENTOS = {
  receita_geral:     (id) => `/financeiro/receitas-gerais/${id}/pagamentos`,
  aula_avulsa:       (id) => `/operacional/aulas-avulsas/${id}/pagamentos`,
  aula_experimental: (id) => `/operacional/aulas-experimentais/${id}/pagamentos`,
  venda:             (id) => `/vendas/${id}/pagamentos`,
};
// Fallback: detalhe raiz (caso não exista /pagamentos)
const FALLBACK_DETALHE = {
  receita_geral:     (id) => [`/financeiro/receitas-gerais/${id}`],
  aula_avulsa:       (id) => [`/operacional/aulas-avulsas/${id}`],
  aula_experimental: (id) => [`/operacional/aulas-experimentais/${id}`],
  venda:             (id) => [`/vendas/${id}`],
};

// ===== Helpers =====
function formatData(d) {
  return d ? (globalUtils?.formatarData?.(d) ?? d) : '—';
}
function formatValor(v) {
  if (v == null) return '—';
  if (globalUtils?.formatarValor) return globalUtils.formatarValor(v);
  try {
    return Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  } catch {
    return String(v);
  }
}
function somaValores(arr) {
  return arr.reduce((acc, n) => acc + (Number(n) || 0), 0);
}
function textoConta(p) {
  const c = contas.value.find(x => x.id === p.conta_financeira_id);
  if (c) return `${c.apelido} | Ag ${c.agencia} | CC ${c.numero_conta}`;
  return p.conta_financeira || '—';
}

// Normaliza respostas possíveis: array; {data: array}; {data:{pagamentos:[]}}; ou objeto unitário
function normalizarComoArray(resData) {
  const d = resData?.data ?? resData;
  if (Array.isArray(d)) return d;
  if (d?.pagamentos && Array.isArray(d.pagamentos)) return d.pagamentos;

  const possivelPgto = {
    id: d?.id,
    valor: d?.valor,
    data_vencimento: d?.data_vencimento,
    data_pagamento: d?.data_pagamento,
    data_recebimento: d?.data_recebimento,
    forma_pagamento: d?.forma_pagamento,
    conta_financeira_id: d?.conta_financeira_id,
    conta_financeira: d?.conta_financeira,
    banco_numero: d?.banco_numero,
    agencia: d?.agencia,
    cheque_numero: d?.cheque_numero,
    parcela: d?.parcela,
  };
  const algumCampo =
    possivelPgto.valor != null ||
    possivelPgto.forma_pagamento != null ||
    possivelPgto.data_pagamento != null ||
    possivelPgto.data_recebimento != null;

  return algumCampo ? [possivelPgto] : [];
}

// ===== Carregamento =====
async function carregarContas() {
  const resp = await axios.get('/financeiro/contas-financeiras');
  const rows = resp.data?.data ?? resp.data ?? [];
  contas.value = rows;
}

async function carregarPagamentos(id) {
  const org = origem.value;
  // A listagem agregada fornece a identidade do pagamento para cada origem.
  if (org !== 'receita_geral' && route.query.pagamento_id) {
    const response = await axios.get(`/financeiro/receitas-completas/${encodeURIComponent(org)}/${encodeURIComponent(route.query.pagamento_id)}`);
    return normalizarComoArray(response.data);
  }

  // 1) Tenta /pagamentos específico da origem
  const makeUrl = Object.hasOwn(ENDPOINT_PAGAMENTOS, org) ? ENDPOINT_PAGAMENTOS[org] : null;
  if (makeUrl) {
    try {
      const r1 = await axios.get(makeUrl(id));
      const arr1 = normalizarComoArray(r1.data);
      if (arr1.length) return arr1;
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('Pagamentos (origem) falhou:', org, e?.message || e);
      }
    }
  }

  // 2) Fallback: detalhe raiz → extrai .pagamentos ou objeto unitário
  const fallbacks = Object.hasOwn(FALLBACK_DETALHE, org) ? FALLBACK_DETALHE[org](id) : [];
  for (const url of fallbacks) {
    try {
      const r = await axios.get(url);
      const arr = normalizarComoArray(r.data);
      if (arr.length) return arr;
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('Fallback detalhe falhou:', url, e?.message || e);
      }
    }
  }

  return [];
}

// ===== Lifecycle =====
onMounted(async () => {
  loading.value = true;
  erro.value = '';
  try {
    await fetchPermissions();
    if (hasPermission('listar_contas_financeiras')) await carregarContas();
    pagamentos.value = await carregarPagamentos(receitaId.value);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
    erro.value = 'Não foi possível carregar os pagamentos desta receita.';
  } finally {
    loading.value = false;
  }
});

// ===== Navegação =====
function voltar() {
  router.back();
}
</script>

<style scoped>
.table td, .table th {
  vertical-align: middle;
}
</style>
